import {useCallback, useEffect, useState} from "react";
import {findHubById} from "../../../api/HubApi";
import {findPipes, updatePipeById} from "../../../api/PipeApi";
import {findBuilds, createBuild, findBuildById, deleteBuildById} from "../../../api/BuildApi";
import {useSnackbarContext} from "../../common/Snackbar";

export default function UseHubDashboard({hubId}) {
    const [hub, setHub] = useState(undefined);
    const [build, setBuild] = useState(undefined);
    const [pipes, setPipes] = useState(undefined);
    const [hubLoading, setHubLoading] = useState(true);
    const [buildLoading, setBuildLoading] = useState(true);
    const [pipesLoading, setPipesLoading] = useState(true);
    const [buildLaunching, setBuildLaunching] = useState(false);
    const [buildStopping, setBuildStopping] = useState(false);

    const {handleMessageUpdate} = useSnackbarContext();

    function handleApiError(error) {
        handleMessageUpdate(`${error.message} [${error.code}]`);
    }

    const handleErrorCallback = useCallback(handleApiError, []);

    useEffect(() => {
        findHubById(hubId)
            .then(hub => {
                handleHubChanged(hub);
                handleHubLoadingChanged(false);
                return findBuilds(hubId, 1);
            })
            .then(builds => {
                const [build] = builds;

                handleBuildChanged(build);
                handleBuildLoadingChanged(false);
                return findPipes(hubId)
            })
            .then(pipes => {
                handlePipesChanged(pipes);
                handlePipesLoadingChanged(false);
                return Promise.resolve();
            })
            .catch(error => {
                handleHubLoadingChanged(false);
                handleBuildLoadingChanged(false);
                handlePipesLoadingChanged(false);
                handleErrorCallback(error);
            });
    }, [hubId, handleErrorCallback]);

    function handleHubChanged(hub) {
        setHub(hub);
    }

    function handleBuildChanged(build) {
        setBuild(build);
    }

    function handlePipesChanged(pipes) {
        setPipes(pipes);
    }

    function handleHubLoadingChanged(value) {
        setHubLoading(value);
    }

    function handleBuildLoadingChanged(value) {
        setBuildLoading(value);
    }

    function handlePipesLoadingChanged(value) {
        setPipesLoading(value);
    }

    function handleReload() {
        findHubById(hubId)
            .then(hub => {
                handleHubChanged(hub);
                handleHubLoadingChanged(false);
                return findPipes(hubId);
            })
            .then(pipes => {
                handlePipesChanged(pipes);
                handlePipesLoadingChanged(false);
                return Promise.resolve();
            })
            .catch(error => {
                handleHubLoadingChanged(false);
                handleBuildLoadingChanged(false);
                handlePipesLoadingChanged(false);
                handleErrorCallback(error);
            });
    }

    function handleBuildLaunch(runnerId = undefined) {
        setBuildLaunching(true);

        return createBuild(hub.id, runnerId)
            .then(build => {
                return findBuildById(hub.id, build.id)
            })
            .then(build => {
                setBuildLaunching(false);
                setBuild(build);
            })
            .catch(error => {
                setBuildLaunching(false);
                handleApiError(error);
            });
    }

    function handleBuildStop() {
        setBuildStopping(true);

        return deleteBuildById(hub.id, build.id)
            .then(() => findBuildById(hub.id, build.id))
            .then(build => {
                setBuildStopping(false);
                setBuild(build);
            })
            .catch(error => {
                setBuildStopping(false);
                handleApiError(error);
            });
    }

    function handleSettingsChanged(pipeId, settings) {
        const newPipes = [...pipes];
        const [pipe] = newPipes.filter(pipe => pipe.id === pipeId);

        pipe.settings = Object.assign({}, pipe.settings, settings);

        setPipes(newPipes);
    }

    function handleSettingsApply(pipeId) {
        const [pipe] = pipes.filter(pipe => pipe.id === pipeId);

        return updatePipeById(hubId, pipeId, {settings: pipe.settings}).catch(error => {
            handleApiError(error);
        });
    }

    function handleActionsApply(pipeId, actions) {
        return updatePipeById(hubId, pipeId, {actions: actions}).catch(error => {
            handleApiError(error);
        });
    }

    return {
        hubLoading,
        buildLoading,
        pipesLoading,
        hub,
        build,
        pipes,
        buildLaunching,
        buildStopping,
        handleReload,
        handleBuildLaunch,
        handleBuildStop,
        handleSettingsChanged,
        handleSettingsApply,
        handleActionsApply
    };
}